import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Unauthorized from "../../Unauthorized";
import PageHeader from "../../PageHeader";
import { toast } from "../../Toaster/toast";
import { blindMaskFrom } from "../../../utils/blindMask";
import RaterReader from "./RaterReader";
import ApplicationHistory from "../../ApplicationHistory/ApplicationHistory";
import {
  DeadlineStrip,
  Distribution,
  RANKS,
  Ring,
  TRACK_IDS,
  TrackCard,
  singular,
  toDate,
} from "./raterParts";
import "./Rater.css";

export { MAX_COMMENT_WORDS, countWords } from "./raterParts";

const COMMITTEE_LEVEL = 80;
const ADMIN_LEVEL = 100;

const errorMessage = (error) => {
  const data = error?.response?.data;
  return (typeof data === "string" ? data : data?.error) || "Something went wrong";
};

const Rater = ({ currentUser }) => {
  const level = currentUser?.Role?.permission_level ?? 0;
  const isAdmin = level >= ADMIN_LEVEL;
  const year = new Date().getFullYear();

  const [chapterId, setChapterId] = useState(() => String(currentUser?.chapter_id || 1));
  const [chapters, setChapters] = useState([]);
  const [overview, setOverview] = useState(null);
  const [denied, setDenied] = useState(false);
  const [failed, setFailed] = useState(false);
  // Skipped applications go to the back of their pile for the rest of the visit.
  const [skipped, setSkipped] = useState([]);
  const [reader, setReader] = useState(null);
  const [view, setView] = useState("desk");

  useEffect(() => {
    setChapterId(String(currentUser?.chapter_id || 1));
  }, [currentUser?.chapter_id]);

  useEffect(() => {
    axios
      .get("/api/chapters")
      .then((r) => setChapters(r.data || []))
      .catch(() => setChapters([]));
  }, []);

  const load = useCallback(() => {
    setFailed(false);
    return axios
      .get(`/api/applicant_rankings/overview?chapter_id=${chapterId}&year=${year}`)
      .then((r) => {
        setDenied(false);
        setOverview(r.data);
      })
      .catch((error) => {
        if (error?.response?.status === 403) setDenied(true);
        else setFailed(true);
      });
  }, [chapterId, year]);

  useEffect(() => {
    if (level < COMMITTEE_LEVEL) return;
    setOverview(null);
    setSkipped([]);
    load();
  }, [load, level]);

  const rows = useMemo(
    () => new Map((overview?.applications || []).map((a) => [a.application_id, a])),
    [overview]
  );

  const tracks = useMemo(() => {
    const apps = overview?.applications || [];
    return TRACK_IDS.map((typeId) => {
      const mine = apps.filter((a) => a.applicant_type_id === typeId);
      const unread = mine.filter((a) => !a.rated).map((a) => a.application_id);
      const pending = [
        ...unread.filter((id) => !skipped.includes(id)),
        ...skipped.filter((id) => unread.includes(id)),
      ];
      return {
        typeId,
        total: mine.length,
        pending,
        rated: mine.filter((a) => a.rated).map((a) => a.application_id),
      };
    });
  }, [overview, skipped]);

  if (level < COMMITTEE_LEVEL || denied) return <Unauthorized />;

  const blind = overview?.blind || { enabled: false };
  const mask = blindMaskFrom(blind);
  const open = !!overview?.open;
  const now = new Date();
  const ratingStart = toDate(overview?.rating_window?.window_start);
  const ratingEnd = toDate(overview?.rating_window?.window_end);
  const hard = toDate(overview?.application_window?.hard_deadline);
  const lateUntil = hard && hard > now ? hard : null;
  const chapter = chapters.find((c) => String(c.id) === chapterId);
  const chapterName = chapter?.location || "";

  const total = tracks.reduce((n, t) => n + t.total, 0);
  const rated = tracks.reduce((n, t) => n + t.rated.length, 0);
  const left = total - rated;
  const upNext = [...tracks].sort((a, b) => b.pending.length - a.pending.length)[0];
  const mine = (overview?.applications || [])
    .filter((a) => a.rated)
    .sort((a, b) => String(b.updated_at || "").localeCompare(String(a.updated_at || "")));
  const dist = RANKS.map((_, i) => mine.filter((a) => a.rating === i).length);

  // Saves the rating, and the note only when it changed, then updates the pile in place.
  const saveRating = async (row, ranking, note) => {
    const comment = note.trim();
    const body = {
      ranking,
      chapter_id: chapterId,
      applicant_type_id: row.applicant_type_id,
      year,
    };
    if (comment !== (row.comment || "").trim()) body.comment = comment;
    try {
      const { data } = await axios.post(`/api/applicant_rankings/${row.application_id}`, body);
      setOverview((o) => ({
        ...o,
        applications: o.applications.map((a) =>
          a.application_id === row.application_id
            ? {
                ...a,
                rated: true,
                rating: ranking,
                ranking_id: data?.id ?? a.ranking_id,
                comment: "comment" in body ? comment : a.comment,
                updated_at: new Date().toISOString(),
              }
            : a
        ),
      }));
      return true;
    } catch (error) {
      toast.error(`Error: ${errorMessage(error)}`, { autoClose: 10000 });
      if (error?.response?.status === 403) load();
      return false;
    }
  };

  const readerTrack = reader && tracks.find((t) => t.typeId === reader.typeId);

  let heroLine = "New applications may still arrive.";
  if (!open && ratingStart && now < ratingStart) heroLine = `Rating opens ${ratingStart.toLocaleDateString()}.`;
  else if (!open) heroLine = "Rating is closed, so your ratings are final.";
  else if (left > 0) heroLine = "Pick up where the most are waiting.";

  return (
    <div className="rr-layout">
      <PageHeader title="Application Rater" />
      <div className="rr-views" role="tablist" aria-label="Application Rater views">
        {[
          ["desk", "My rating desk"],
          ["history", "History"],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={view === key}
            className={view === key ? "active" : ""}
            onClick={() => setView(key)}
          >
            {label}
          </button>
        ))}
      </div>
      {view === "history" ? (
        <div className="rr-page">
          <ApplicationHistory defaultChapterId={Number(chapterId)} />
        </div>
      ) : !overview ? (
        <div className="rr-loading" role="status">
          {failed ? (
            <>
              We couldn&apos;t load your ratings.{" "}
              <button type="button" className="rr-link" onClick={load}>
                Try again
              </button>
            </>
          ) : (
            "Loading your rating desk…"
          )}
        </div>
      ) : (
        <div className="rr-page">
          <section className="rr-hero">
            <Ring done={rated} total={total} size={168} stroke={16} accent="rainbow">
              <b>{left}</b>
              <small>left to read</small>
            </Ring>
            <div className="rr-hero-text">
              <span className="rr-pill">
                <i aria-hidden="true">📍</i> {chapterName ? `${chapterName} chapter · ` : ""}
                {overview.year} cohort
              </span>
              <h1>{total > 0 && left === 0 ? "You're all caught up 🎉" : "Your rating desk"}</h1>
              <p>
                You&apos;ve rated <b>{rated}</b> of {total} applications. {heroLine}
              </p>
              {open && ratingEnd && (
                <p className="rr-muted">
                  ✏️ You can go back and change any rating until rating closes on {ratingEnd.toLocaleDateString()}.
                </p>
              )}
              <p className="rr-muted">
                {blind.enabled
                  ? "🙈 Blind review is on: names and the fields your chapter chose are hidden."
                  : "👀 Blind review is off for your chapter."}{" "}
                Your own application is never in your pile.
              </p>
              {isAdmin && chapters.length > 0 && (
                <div className="rr-hero-tools">
                  <label htmlFor="rr-chapter">Chapter</label>
                  <select id="rr-chapter" value={chapterId} onChange={(e) => setChapterId(e.target.value)}>
                    {chapters.map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {c.location}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </section>

          {!open && (
            <p className="rr-banner" role="alert">
              {ratingStart && now < ratingStart
                ? `Rating hasn't opened yet. You can start on ${ratingStart.toLocaleDateString()}.`
                : "Rating is closed for this chapter. You can still look back through your ratings."}
            </p>
          )}

          <DeadlineStrip
            applicationWindow={overview.application_window}
            ratingWindow={overview.rating_window}
            now={now}
          />

          <div className="rr-tracks">
            {tracks.map((t) => (
              <TrackCard
                key={t.typeId}
                track={t}
                upNext={open && t.typeId === upNext.typeId && t.pending.length > 0}
                open={open}
                lateUntil={lateUntil}
                onBegin={() => setReader({ mode: "queue", typeId: t.typeId })}
                onReview={() => setReader({ mode: "review", typeId: t.typeId, startId: t.rated[0] })}
              />
            ))}
          </div>

          <div className="rr-lower">
            <section className="rr-mine" aria-label="My ratings">
              <h3>My ratings</h3>
              {mine.length === 0 ? (
                <p className="rr-mine-empty">You haven&apos;t rated anyone yet.</p>
              ) : (
                <ul>
                  {mine.map((m) => (
                    <li key={m.application_id}>
                      <button
                        type="button"
                        onClick={() =>
                          setReader({ mode: "review", typeId: m.applicant_type_id, startId: m.application_id })
                        }
                      >
                        <span className="rr-mine-who">
                          {m.label}
                          <small>{singular(m.applicant_type_id)}</small>
                        </span>
                        {m.comment && (
                          <span className="rr-mine-note" title={m.comment} aria-label="Has a note">
                            ✎
                          </span>
                        )}
                        <span className={`rr-chip rr-chip-sm lit rr-rank-${m.rating}`}>{RANKS[m.rating]}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <Distribution dist={dist} />
          </div>
        </div>
      )}

      {reader && readerTrack && (
        <RaterReader
          key={`${reader.mode}-${reader.typeId}-${reader.startId ?? ""}`}
          mode={reader.mode}
          track={readerTrack}
          startId={reader.startId}
          rows={rows}
          mask={mask}
          chapterName={chapterName}
          open={open}
          lateUntil={lateUntil}
          currentUser={currentUser}
          onSave={saveRating}
          onSkip={(id) => setSkipped((s) => [...s.filter((x) => x !== id), id])}
          onReview={(typeId) => {
            const t = tracks.find((x) => x.typeId === typeId);
            setReader({ mode: "review", typeId, startId: t?.rated[0] });
          }}
          onClose={() => {
            setReader(null);
            load();
          }}
        />
      )}
    </div>
  );
};

export default Rater;
